import * as React from 'react'
import { FunctionalMonitoringIframe } from '../FunctionalMonitoring/Iframe/FunctionalMonitoringIframe'
import { AnalyticsLandingPage } from '../AnalyticsLandingPage/AnalyticsLandingPage'
import { destinationConnectors, sourceConnectors } from '../Tools/connectors'
import { CustomMetricsPage } from '../CustomMetrics/CustomMetricsPage'
import { sendMetricsConnectors } from '../CustomMetrics/connectors'
import { LogPointsType } from '../LogPoints/LogPointsPage/model'
import { RouteComponentProps } from 'react-router'
import { ToolsPage } from '../Tools/ToolsPage'
import { Profile, RoleTypes } from '../../lifecycle/auth'
import BuiltInDashboardLandingPageContainer from '../BuiltInDashboard/BuiltInDashboardLandingPage/BuiltInDashboardLandingPageContainer'
import CustomDashboardListContainer from '../CustomDashboard/List/CustomDashboardListContainer'
import MyCustomDashboardContainer from '../CustomDashboard/Item/MyCustomDashboardContainer'
import BuiltInDashboardContainer from '../BuiltInDashboard/BuiltInDashboardContainer'
import LogPointsLanding from '../LogPoints/LogPointsLanding/LogPointsLandingContainer'
import LoggingIframeContainer from '../LogSearch/Iframe/LogSearchIframeContainer'
import RawDataLandingPageContainer from '../RawData/RawDataLandingPageContainer'
import ReportsLandingPageContainer from '../Reports/ReportsLandingPageContainer'
import EmptyStatePageContainer from '../EmptyStatePage/EmptyStatePageContainer'
import AlertsPageHandlerContainer from '../Alerts/AlertsPageHandlerContainer'
import LogPointsPage from '../LogPoints/LogPointsPage/LogPointsPageContainer'
import SettingsPageContainer from '../Settings/SettingsPageContainer'
import RawDataPageContainer from '../RawData/RawDataPageContainer'
import ReportsPageContainer from '../Reports/ReportsPageContainer'
import LifeCycleErrorChecker from './LifeCycleErrorChecker'
import SyncChecker from './SyncChecker'
import Urls from './Urls'
import {
  validateAPILogPoint,
  validateAPIParams,
  validateAPPLogPoint,
  validateAPPParams,
  validateId,
  validateParam,
  validateReportsParams,
  validateServerParams,
  validateSettingsParams
} from './RouteParamsValidator'
import { RawDataResourceType } from '../RawData/model'
import FeatureFlagService from '../../services/FeatureFlagService';
import FeatureFlags from '../../types/featureflags';
import OsdLogsSearchPageModeEnum from '../../types/OsdLogsSearchPageModeEnum';
import PermissionsChecker from './PermissionsChecker';

export type MonitoringRoute = {
  url: string,
  label: string,
  component?: React.ComponentType<RouteComponentProps<any>> | React.ComponentType<any>,
  isVisible: (e: Profile) => boolean,
  shouldDisplay: boolean,
  hideForPCE?: boolean,
  hideForGovCloud?: boolean,
  exact?: boolean,
  children?: MonitoringRoute[],
  roles?: RoleTypes[],
  validateRoute?: (url: { match: any }) => boolean,
  urlResolver?: (e: Profile) => string,
}

const modernUILinkEnabled = () => () => {
  const isFeatureFlagEnabled = FeatureFlagService.get(FeatureFlags.ENTITIES_OVERVIEW_LINK_ENABLED);
  // This check for the LD flag being enabled is very important because Insights is not available in Gov-cloud.
  // Using LD flag helps hide Insights entry in the Side nav in mon-center-ui in gov-cloud.
  return isFeatureFlagEnabled;
}
const tracesOverviewLinkEnabled = () => (profile: Profile) => {
  const isFeatureFlagEnabled = FeatureFlagService.get(FeatureFlags.TRACES_OVERVIEW_CLASSIC_UI_SIDE_NAV_LINK_ENABLED)
  const hasLicense = hasPremiumSKU()(profile);
  return isFeatureFlagEnabled && hasLicense;
}
const telemetryExporterLinkEnabled = () => (profile: Profile) => {
  const isFeatureFlagEnabled = FeatureFlagService.get(FeatureFlags.TELEMETRY_EXPORTER_CLASSIC_UI_SIDE_NAV_LINK_ENABLED)
  const hasLicense = hasPremiumSKU()(profile);
  return isFeatureFlagEnabled && hasLicense;
}

const modernUIRouteSetter = (url: { match: any }) => {
  // Directly set the window location to take the user to the new url.
  // Use the url available in the `match` object provided by React Router.
  // Reference: https://v5.reactrouter.com/web/api/match
  window.location.assign(url.match.url);
  return null;
}

const redirectToModernMonitoringIfClassicDisabled = (): boolean => {
  if (FeatureFlagService.get(FeatureFlags.CLASSIC_BUILT_IN_DASHBOARDS_PAGE_DISABLED)) {
    window.location.assign(Urls.modernMonitoring);
    return true;
  }
  return false;
}

const hasStarterSKU = () => {
  return (e: Profile) => {
    const sku = getSKU(e)
    return sku > -1
  }
}

const hasFreeSKU = () => {
  return (e: Profile) => {
    const sku = getSKU(e)
    return sku > -1 && sku !== 3
  }
}

const getSKU = (e: Profile) => {
  return e.organization.entitlements.monitoringCenter.productSKU;
}

const hasPremiumSKU = () => {
  return (e: Profile) => {
    const sku = getSKU(e)
    return sku > 0  && sku !== 3
  }
}

const isAFMEnabled = () => {
  return (e: Profile) => e.organization.entitlements.apiMonitoring.schedules > 0
}

const noVisible = () => {
  return (e: Profile) => false
}

export const monitoringRouteTree: MonitoringRoute[] = [
  {
    url: Urls.builtIn,
    label: 'Built-in dashboards',
    component: () => {
      if (redirectToModernMonitoringIfClassicDisabled()) {
        return null;
      }
      return (
        <SyncChecker>
          <BuiltInDashboardLandingPageContainer/>
        </SyncChecker>
      );
    },
    isVisible: hasStarterSKU(),
    shouldDisplay: true,
    children: [
      {
        url: `${Urls.builtIn}/:env/:resourceType/:id/:dashboardType`,
        validateRoute: (url: { match: any }) => validateAPPParams(url.match.params.env, url.match.params.resourceType, url.match.params.id,
                                                                  url.match.params.dashboardType),
        label: 'Built-in Dashboard',
        component: (url: { match: any }) => {
          if (redirectToModernMonitoringIfClassicDisabled()) {
            return null;
          }
          return (
            <SyncChecker>
              <BuiltInDashboardContainer
                environmentId={`${url.match.params.env}`}
                resourceId={`${url.match.params.id}`}
                resourceType={`${url.match.params.resourceType}`}
                dashboardType={`${url.match.params.dashboardType}`}
              />
            </SyncChecker>
          );
        },
        isVisible: hasStarterSKU(),
        shouldDisplay: false,
        exact: true,
        children: []
      },
      {
        url: `${Urls.builtIn}/:env/:resourceType/:id/:targetId/:dashboardType`,
        validateRoute: (url: { match: any }) => validateServerParams(url.match.params.env, url.match.params.resourceType, url.match.params.id,
                                                                     url.match.params.targetId, url.match.params.dashboardType),
        label: 'Built-in Dashboard',
        component: (url: { match: any }) => {
          if (redirectToModernMonitoringIfClassicDisabled()) {
            return null;
          }
          return (
            <SyncChecker>
              <BuiltInDashboardContainer
                environmentId={`${url.match.params.env}`}
                resourceId={`${url.match.params.id}`}
                targetId={url.match.params.targetId}
                resourceType={`${url.match.params.resourceType}`}
                dashboardType={`${url.match.params.dashboardType}`}
              />
            </SyncChecker>
          );
        },
        isVisible: hasStarterSKU(),
        shouldDisplay: false,
        exact: true,
        children: []
      },
      {
        url: `${Urls.builtIn}/:env/:resourceType/:resourceId/version/:versionId/:dashboardType`,
        validateRoute: (url: { match: any }) => validateAPIParams(url.match.params.env, url.match.params.resourceId, url.match.params.versionId,
                                                                  url.match.params.dashboardType),
        label: 'Built-in Dashboard',
        component: (url: { match: any }) => {
          if (redirectToModernMonitoringIfClassicDisabled()) {
            return null;
          }
          return (
            <SyncChecker>
              <BuiltInDashboardContainer
                environmentId={`${url.match.params.env}`}
                resourceId={`${url.match.params.resourceId}`}
                versionId={`${url.match.params.versionId}`}
                resourceType={`${url.match.params.resourceType}`}
                dashboardType={`${url.match.params.dashboardType}`}
              />
            </SyncChecker>
          );
        },
        isVisible: hasStarterSKU(),
        shouldDisplay: false,
        exact: true,
        children: []
      }
    ]
  },
  {
    url: Urls.newCustomDashboardsListPage,
    label: 'Custom dashboards',
    isVisible: (profile: Profile) => hasFreeSKU()(profile) && FeatureFlagService.get(FeatureFlags.CLASSIC_CUSTOM_DASHBOARDS_PAGE_DISABLED),
    shouldDisplay: false,
    urlResolver: (e: Profile) => Urls.newCustomDashboardsListPage.replace(':orgId', e.organizationId),
    component: modernUIRouteSetter
  },
  {
    url: Urls.customDashboardPage,
    label: 'Custom dashboards',
    component: () => {
      if (FeatureFlagService.get(FeatureFlags.CLASSIC_CUSTOM_DASHBOARDS_PAGE_DISABLED)) {
        window.location.assign(
          Urls.newCustomDashboardsListPage.replace(':orgId', window.mc.profile.properties.cs_auth.activeOrganizationId)
        );
        return null;
      }
      return (
        <SyncChecker>
          <CustomDashboardListContainer/>
        </SyncChecker>
      );
    },
    isVisible: hasFreeSKU(),
    shouldDisplay: true,
    urlResolver: (e: Profile) => FeatureFlagService.get(FeatureFlags.CLASSIC_CUSTOM_DASHBOARDS_PAGE_DISABLED)
      ? Urls.newCustomDashboardsListPage.replace(':orgId', e.organizationId)
      : Urls.customDashboardPage,
    children: [
      {
        url: Urls.newCustomDashboard,
        label: 'New Custom Dashboard',
        component: () => (
          <SyncChecker>
            <MyCustomDashboardContainer
              src={`/monitoring/api/visualizer/dashboard/new`}
              id={`new-custom-dashboard-iframe`}
            />
          </SyncChecker>
        ),
        isVisible: (profile: Profile) => hasFreeSKU()(profile) && !FeatureFlagService.get(FeatureFlags.CLASSIC_CUSTOM_DASHBOARDS_PAGE_DISABLED),
        shouldDisplay: false,
        exact: true,
        children: [],
        roles: [RoleTypes.ADMIN],
      },
      {
        url: `${Urls.customDashboardPage}/:uri`,
        validateRoute: (url: { match: any }) => validateParam(url.match.params.uri),
        label: 'My Custom Dashboard',
        component: (url: { match: any; location?: { search?: string } }) => {
          if (FeatureFlagService.get(FeatureFlags.CLASSIC_CUSTOM_DASHBOARDS_PAGE_DISABLED)) {
            const orgId = window.mc.profile.properties.cs_auth.activeOrganizationId;
            const search = (url.location && url.location.search) || '';
            const dashboardsListUrl = Urls.newCustomDashboardsListPage.replace(':orgId', orgId);
            window.location.assign(`${dashboardsListUrl}/${url.match.params.uri}${search}`);
            return null;
          }
          return (
            <SyncChecker>
              <MyCustomDashboardContainer
                src={`/monitoring/api/visualizer/dashboard/db/${url.match.params.uri}`}
                id={`my-custom-dashboard-iframe`}
              />
            </SyncChecker>
          );
        },
        isVisible: hasFreeSKU(),
        shouldDisplay: false,
        children: [],
        exact: false
      }
    ]
  },
  {
    url: Urls.modernMonitoring,
    label: 'Insights',
    isVisible: modernUILinkEnabled(),
    shouldDisplay: true,
    component: () => {
      window.location.assign('/monitoring-new');
      return null;
    }
  },
  {
    url: Urls.tracesOverview,
    label: 'Traces',
    isVisible: tracesOverviewLinkEnabled(),
    shouldDisplay: true,
    urlResolver: (e: Profile) => Urls.tracesOverview.replace(':orgId', e.organizationId),
    component: modernUIRouteSetter
  },
  {
    url: Urls.telemetryExporter,
    label: 'Telemetry Exporter',
    isVisible: telemetryExporterLinkEnabled(),
    shouldDisplay: true,
    urlResolver: (e: Profile) => Urls.telemetryExporter.replace(':orgId', e.organizationId),
    component: modernUIRouteSetter,
  },
  {
    url: Urls.newReportsPage,
    label: 'Reports',
    isVisible: (profile: Profile) => hasPremiumSKU()(profile) && FeatureFlagService.get(FeatureFlags.CLASSIC_REPORTS_PAGE_DISABLED),
    shouldDisplay: true,
    urlResolver: (e: Profile) => Urls.newReportsPage.replace(':orgId', e.organizationId),
    component: modernUIRouteSetter
  },
  {
    url: Urls.reports,
    label: 'Reports',
    component: () => (
      <SyncChecker>
        <ReportsLandingPageContainer/>
      </SyncChecker>
    ),
    isVisible: (profile: Profile) => hasPremiumSKU()(profile) && !FeatureFlagService.get(FeatureFlags.CLASSIC_REPORTS_PAGE_DISABLED),
    shouldDisplay: true,
    children: [
      {
        url: `${Urls.reports}/organizations/:businessGroup/environments/:environment/from/:from/to/:to/type/:selectedType`,
        validateRoute: (url: { match: any }) => validateReportsParams(url.match.params.businessGroup, url.match.params.environment,
                                                                      url.match.params.from, url.match.params.to, url.match.params.selectedType),
        label: 'Reports',
        component: (url: { match: any }) => (
          <SyncChecker>
            <ReportsPageContainer
              selectedBusinessGroup={`${url.match.params.businessGroup}`}
              selectedEnvironment={`${url.match.params.environment}`}
              selectedType={`${url.match.params.selectedType}`}
              from={`${url.match.params.from}`}
              to={`${url.match.params.to}`}
            />
          </SyncChecker>
        ),
        isVisible: (profile: Profile) => hasPremiumSKU()(profile) && !FeatureFlagService.get(FeatureFlags.CLASSIC_REPORTS_PAGE_DISABLED),
        shouldDisplay: false,
        children: [],
        exact: false
      }
    ]
  },
  {
    url: Urls.alerts,
    label: 'Alerts',
    component: () => (
      <SyncChecker>
        <AlertsPageHandlerContainer/>
      </SyncChecker>
    ),
    exact: true,
    isVisible: hasFreeSKU(),
    shouldDisplay: true,
    children: [
      {
        url: `${Urls.alerts}/:alertId`,
        validateRoute: (url: { match: any }) => validateId(url.match.params.alertId),
        label: 'Alerts',
        component: (url: { match: any }) => (
          <SyncChecker>
            <AlertsPageHandlerContainer alertId={`${url.match.params.alertId}`}/>
          </SyncChecker>
        ),
        isVisible: hasFreeSKU(),
        shouldDisplay: false,
        exact: false,
        children: []
      }
    ]
  },
  {
    url: Urls.customMetrics,
    label: 'Custom Metrics',
    component: () => (
      <SyncChecker>
        <CustomMetricsPage sendMetricsConnectors={sendMetricsConnectors}/>
      </SyncChecker>
    ),
    exact: true,
    isVisible: hasPremiumSKU(),
    shouldDisplay: true,
    hideForPCE: true,
    hideForGovCloud: true,
  },
  {
    url: Urls.apitesting,
    label: 'Functional Monitoring',
    component: () => (
      <FunctionalMonitoringIframe
        id="functional-monitoring-iframe"
        src={`/apitesting/ui/`}
      />
    ),
    exact: true,
    isVisible: isAFMEnabled(),
    shouldDisplay: true,
    hideForPCE: true,
    hideForGovCloud: true,
    children: [
      {
        url: `${Urls.apitesting}/:landing`,
        label: 'Functional Monitoring Landing',
        exact: true,
        children: [],
        isVisible: isAFMEnabled(),
        shouldDisplay: true,
        hideForPCE: true,
        hideForGovCloud: true,
        component: (url: { match: any }) => (
          <FunctionalMonitoringIframe
            id="functional-monitoring-iframe"
            src={`/apitesting/ui/#/${url.match.params.landing}/`}
          />
        ),
      },
      {
        url: `${Urls.apitesting}/suite/:suiteId`,
        label: 'Functional Monitoring - Suite',
        exact: true,
        children: [],
        isVisible: isAFMEnabled(),
        shouldDisplay: true,
        hideForPCE: true,
        hideForGovCloud: true,
        component: (url: { match: any }) => (
          <FunctionalMonitoringIframe
            id="functional-monitoring-iframe"
            src={`/apitesting/ui/#/suite/${url.match.params.suiteId}/`}
          />
        ),
      },
      {
        url: `${Urls.apitesting}/suite/:suiteId/version/:version/execution/:executionId`,
        label: 'Functional Monitoring - Execution',
        exact: true,
        children: [],
        isVisible: isAFMEnabled(),
        shouldDisplay: true,
        hideForPCE: true,
        hideForGovCloud: true,
        component: (url: { match: any }) => {
          const {suiteId, version, executionId} = url.match.params
          const route = `/apitesting/ui/#/suite/${suiteId}/version/${version}/execution/${executionId}/`
          return (
            <FunctionalMonitoringIframe
              id="functional-monitoring-iframe"
              src={route}
            />
          )
        }
      }
    ]
  }
]

export const logManagementRouteTree: MonitoringRoute[] = [
  {
    url: Urls.logs,
    label: 'Log Search',
    component: () => {
      const mode = FeatureFlagService.getStringFlag(FeatureFlags.OSD_LOG_SEARCH_PAGE_MODE);
      const kibanaEnabled = FeatureFlagService.get(FeatureFlags.MC_KIBANA_LOG_SEARCH_ENABLED);
      if (!kibanaEnabled && mode === OsdLogsSearchPageModeEnum.ENABLED) {
        window.location.assign(Urls.osdLogsSearch.replace(':orgId', window.mc.profile.properties.cs_auth.activeOrganizationId));
        return null;
      }
      return (
        <PermissionsChecker>
          <LifeCycleErrorChecker>
            <LoggingIframeContainer/>
          </LifeCycleErrorChecker>
        </PermissionsChecker>
      );
    },
    exact: true,
    isVisible: hasPremiumSKU(),
    shouldDisplay: true,
    hideForPCE: true,
    hideForGovCloud: true,
  },
  {
    url: Urls.newLogPointsPage,
    label: 'Log Points',
    isVisible: (profile: Profile) => hasPremiumSKU()(profile) && FeatureFlagService.get(FeatureFlags.CLASSIC_LOGPOINTS_PAGE_DISABLED),
    shouldDisplay: true,
    urlResolver: (e: Profile) => Urls.newLogPointsPage.replace(':orgId', e.organizationId),
    component: modernUIRouteSetter
  },   
  {
    url: Urls.logPoints,
    label: 'Log Points',
    component: () => {
        if (FeatureFlagService.get(FeatureFlags.CLASSIC_LOGPOINTS_PAGE_DISABLED)) {
            window.location.assign(
                Urls.newLogPointsPage.replace(':orgId', window.mc.profile.properties.cs_auth.activeOrganizationId)
            );
            return null;
        }      
        return (
          <SyncChecker><LogPointsLanding/></SyncChecker>
        );
    },
    isVisible: (profile: Profile) => hasPremiumSKU()(profile) && !FeatureFlagService.get(FeatureFlags.CLASSIC_LOGPOINTS_PAGE_DISABLED),
    shouldDisplay: true,
    hideForPCE: true,
    hideForGovCloud: true,
    children: [
      {
        url: `${Urls.logPoints}/environments/:environmentId/apis/:apiId/versions/:versionId`,
        validateRoute: (url: { match: any }) => validateAPILogPoint(url.match.params.environmentId, url.match.params.apiId,
                                                                    url.match.params.versionId),
        label: 'API Log Points',
        exact: true,
        children: [],
        isVisible: hasPremiumSKU(),
        shouldDisplay: false,
        hideForPCE: true,
        hideForGovCloud: true,
        component: (url: { match: any }) => {
          const environmentId = url.match.params.environmentId
          const resourceId = url.match.params.apiId
          const versionId = url.match.params.versionId
          const props = {environmentId, resourceId, versionId, type: LogPointsType.API}

          return (
            <SyncChecker>
              <LogPointsPage {...props}/>
            </SyncChecker>
          )
        }
      },
      {
        url: `${Urls.logPoints}/environments/:environmentId/applications/:appId/:targetId?`,
        validateRoute: (url: { match: any }) => validateAPPLogPoint(url.match.params.environmentId, url.match.params.appId,
                                                                    url.match.params.targetId),
        label: 'APP Log Points',
        exact: true,
        children: [],
        isVisible: window.mc && window.mc.featureFlags.logPoints.applications.enabled === 'true' ? hasPremiumSKU() : noVisible(),
        shouldDisplay: false,
        hideForPCE: true,
        hideForGovCloud: true,
        component: (url: { match: any }) => {
          const environmentId = url.match.params.environmentId
          const resourceId = url.match.params.appId
          const targetId = url.match.params.targetId
          const props = {environmentId, resourceId, targetId, type: LogPointsType.APPLICATION}

          return (
            <SyncChecker>
              <LogPointsPage {...props}/>
            </SyncChecker>
          )
        }
      }
    ]
  },
  {
    url: Urls.newRawDataPage,
    label: 'Raw Data',
    isVisible: (profile: Profile) => hasPremiumSKU()(profile) && FeatureFlagService.get(FeatureFlags.CLASSIC_RAW_DATA_PAGE_DISABLED),
    shouldDisplay: true,
    urlResolver: (e: Profile) => Urls.newRawDataPage.replace(':orgId', e.organizationId),
    component: modernUIRouteSetter
  },
  {
    url: Urls.rawData,
    label: 'Raw Data',
    component: () => {
      if (FeatureFlagService.get(FeatureFlags.CLASSIC_RAW_DATA_PAGE_DISABLED)) {
        window.location.assign(
          Urls.newRawDataPage.replace(':orgId', window.mc.profile.properties.cs_auth.activeOrganizationId)
        );
        return null;
      }
      return (
        <SyncChecker>
          <RawDataLandingPageContainer/>
        </SyncChecker>
      );
    },
    isVisible: (profile: Profile) => hasPremiumSKU()(profile) && !FeatureFlagService.get(FeatureFlags.CLASSIC_RAW_DATA_PAGE_DISABLED),
    shouldDisplay: true,
    hideForPCE: true,
    hideForGovCloud: true,
    urlResolver: (e: Profile) => FeatureFlagService.get(FeatureFlags.CLASSIC_RAW_DATA_PAGE_DISABLED)
      ? Urls.newRawDataPage.replace(':orgId', e.organizationId)
      : Urls.rawData,
    children: [
      {
        url: `${Urls.rawData}/environments/:envId/:resourceType/:resourceId`,
        validateRoute: (url: { match: any }) => validateId(url.match.params.envId),
        label: 'Raw Data',
        component: (url: { match: any, location: any }) => {
          if (FeatureFlagService.get(FeatureFlags.CLASSIC_RAW_DATA_PAGE_DISABLED)) {
            window.location.assign(
              Urls.newRawDataPage.replace(':orgId', window.mc.profile.properties.cs_auth.activeOrganizationId)
            );
            return null;
          }
          const date = url.location.search.split('=')[1]

          return (
              <SyncChecker>
                <RawDataPageContainer
                    environmentId={`${url.match.params.envId}`}
                    resourceType={RawDataResourceType[`${url.match.params.resourceType}` as keyof typeof RawDataResourceType]}
                    resourceId={`${url.match.params.resourceId}`}
                    date={date}
                />
              </SyncChecker>
          )
        },
        isVisible: (profile: Profile) => hasPremiumSKU()(profile) && !FeatureFlagService.get(FeatureFlags.CLASSIC_RAW_DATA_PAGE_DISABLED),
        shouldDisplay: false,
        hideForPCE: true,
        hideForGovCloud: true,
        exact: false
      }
    ]
  }
]

export const otherRouteTree: MonitoringRoute[] = [
    {
        url: Urls.newToolsPage,
        label: 'Tools',
        component: modernUIRouteSetter,
        exact: true,
        isVisible: (profile: Profile) => hasPremiumSKU()(profile) && FeatureFlagService.get(FeatureFlags.CLASSIC_TOOLS_PAGE_DISABLED),
        urlResolver: (e: Profile) => Urls.newToolsPage.replace(':orgId', e.organizationId),
        shouldDisplay: true,
        hideForPCE: true,
        roles: [RoleTypes.ADMIN],
      },    
  {
    url: Urls.tools,
    label: 'Tools',
    component: () => {
       if (FeatureFlagService.get(FeatureFlags.CLASSIC_TOOLS_PAGE_DISABLED)) {
        window.location.assign(
            Urls.newToolsPage.replace(':orgId', window.mc.profile.properties.cs_auth.activeOrganizationId)
        );
        return null;
       }         
      return (    
        <SyncChecker>
            <ToolsPage destinationConnectors={destinationConnectors} sourceConnectors={sourceConnectors}/>
        </SyncChecker>
      );
    },
    exact: true,
    isVisible: (profile: Profile) => hasPremiumSKU()(profile) && !FeatureFlagService.get(FeatureFlags.CLASSIC_TOOLS_PAGE_DISABLED),
    shouldDisplay: true,
    hideForPCE: true,
    roles: [RoleTypes.ADMIN],
  },
  {
    url: Urls.newSettingsPage,
    label: 'Settings',
    isVisible: (profile: Profile) => hasPremiumSKU()(profile) && FeatureFlagService.get(FeatureFlags.CLASSIC_SETTINGS_PAGE_DISABLED),
    shouldDisplay: true,
    urlResolver: (e: Profile) => Urls.newSettingsPage.replace(':orgId', e.organizationId),
    component: modernUIRouteSetter
  },  
  {
    url: Urls.settings,
    label: 'Settings',
    component: () => {
      if (FeatureFlagService.get(FeatureFlags.CLASSIC_SETTINGS_PAGE_DISABLED)) {
        window.location.assign(
            Urls.newSettingsPage.replace(':orgId', window.mc.profile.properties.cs_auth.activeOrganizationId)
        );
        return null;
      }        
      return (    
        <SyncChecker>
            <SettingsPageContainer/>
        </SyncChecker>
      )
    },
    exact: true,
    isVisible: (profile) => hasStarterSKU()(profile) && !FeatureFlagService.get(FeatureFlags.CLASSIC_SETTINGS_PAGE_DISABLED),
    shouldDisplay: true,
    roles: [RoleTypes.ADMIN],
    children: [
      {
        url: `${Urls.settings}/:category`,
        validateRoute: (url: { match: any }) => validateParam(url.match.params.category),
        label: 'Settings',
        component: (url: { match: any }) => (
          <SyncChecker>
            <SettingsPageContainer selectedCategory={`${url.match.params.category}`}/>
          </SyncChecker>
        ),
        isVisible: hasStarterSKU(),
        shouldDisplay: false,
        roles: [RoleTypes.ADMIN],
        exact: false
      },
      {
        url: `${Urls.settings}/:category/environments/:envId`,
        validateRoute: (url: { match: any }) => validateSettingsParams(url.match.params.category, url.match.params.envId),
        label: 'Settings',
        component: (url: { match: any }) => (
          <SyncChecker>
            <SettingsPageContainer selectedCategory={`${url.match.params.category}`} selectedEnvironment={`${url.match.params.envId}`}/>
          </SyncChecker>
        ),
        isVisible: hasStarterSKU(),
        shouldDisplay: false,
        roles: [RoleTypes.ADMIN],
        exact: false
      }
    ]
  }
]

const text1 = 'Either it doesn\'t exist or it was deleted. Contact your organization administrator for more information.'

const routeTree: MonitoringRoute[] = monitoringRouteTree.concat(logManagementRouteTree).concat(otherRouteTree).concat([
  {
    url: Urls.analyticsLandingPage,
    label: 'Api Metrics Welcome',
    component: () => (
      <SyncChecker>
        <AnalyticsLandingPage/>
      </SyncChecker>
    ),
    exact: true,
    isVisible: hasStarterSKU(),
    shouldDisplay: false,
    hideForPCE: true,
    hideForGovCloud: true
  },
  {
    url: Urls.emptyStatePage,
    label: 'Api Metrics Welcome',
    component: () => (
      <SyncChecker>
        <EmptyStatePageContainer/>
      </SyncChecker>
    ),
    exact: true,
    isVisible: hasStarterSKU(),
    shouldDisplay: false,
    hideForPCE: true,
    hideForGovCloud: true
  }
])

const flatten = (original: MonitoringRoute[]): MonitoringRoute[] => {
  const arr = [...original]

  for (let o of original) {
    const children = o.children
    if (children) {
      arr.push(...flatten(children))
    }
  }
  return arr
}

export const flatRoutes = flatten(routeTree)

export const routesWithoutSideBar: string[] = [
  Urls.analyticsLandingPage.toString(),
  Urls.emptyStatePage.toString()
]

export const routesWithDefaultAnypointNavBar: string[] = [
  Urls.analyticsLandingPage.toString(),
  Urls.emptyStatePage.toString()
]

export default routeTree
